import { SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Card, CardContent } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate } from '@tanstack/react-router'
import AdminReports from '@web/src/features/admin/admin-reports/admin-reports'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  ADMIN_REPORT_CASE_VIEWS,
  ALL_TARGET_KINDS,
  REPORT_LEGAL_TRACK_VALUES,
  REPORT_REASON_VALUES,
} from '@web/src/lib/reports/catalog'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useCallback } from 'react'
import type {
  AdminReportCaseViewEnum,
  ReportLegalTrackEnum,
  ReportReasonEnum,
  ReportTargetKindEnum,
} from 'types/graphql'
import { z } from 'zod'

const emptyToUndefined = <T extends z.ZodType>(schema: T) =>
  z.preprocess((value) => (value === '' || value == null ? undefined : value), schema.optional())

const reportsSearchSchema = z.object({
  view: emptyToUndefined(z.enum(ADMIN_REPORT_CASE_VIEWS)),
  name: z.string().optional(),
  subjectProfileId: z.string().optional(),
  reason: emptyToUndefined(z.enum(REPORT_REASON_VALUES)),
  legalTrack: emptyToUndefined(z.enum(REPORT_LEGAL_TRACK_VALUES)),
  targetKind: emptyToUndefined(z.enum(ALL_TARGET_KINDS)),
  slaOverdue: z.boolean().optional(),
})

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/reports/')({
  component: ReportsPage,
  validateSearch: reportsSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        view: 'UNRESOLVED',
        name: '',
        subjectProfileId: '',
        slaOverdue: false,
      }),
    ],
  },
  head: () =>
    seo({
      title: `Admin - Reports | ${env.VITE_APP_NAME}`,
      description: 'Admin Reports Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
})

function ReportsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = Route.useSearch()

  const params = {
    view: (search.view as AdminReportCaseViewEnum | undefined) || 'UNRESOLVED',
    name: search.name || '',
    subjectProfileId: search.subjectProfileId || '',
    reason: (search.reason as ReportReasonEnum | undefined) || '',
    legalTrack: (search.legalTrack as ReportLegalTrackEnum | undefined) || '',
    targetKind: (search.targetKind as ReportTargetKindEnum | undefined) || '',
    slaOverdue: Boolean(search.slaOverdue),
  }

  const formMethods = useForm({
    defaultValues: {
      view: params.view,
      name: params.name,
      reason: params.reason,
      legalTrack: params.legalTrack,
      targetKind: params.targetKind,
    },
  })

  const filterResults = useCallback(() => {
    navigate({
      search: {
        ...search,
        view: formMethods.getValues('view'),
        name: String(formMethods.getValues('name') ?? '').trim() || undefined,
        reason: formMethods.getValues('reason') || undefined,
        legalTrack: formMethods.getValues('legalTrack') || undefined,
        targetKind: formMethods.getValues('targetKind') || undefined,
      },
    } as never)
  }, [formMethods, navigate, search])

  const handleNameSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          name: value.trim() || undefined,
        },
      } as never)
    },
    [navigate, search]
  )

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col">
        <Form
          className="w-full"
          formMethods={formMethods}
          onSubmit={(data) => {
            navigate({
              search: {
                ...search,
                view: data.view,
                name: String(data.name ?? '').trim() || undefined,
                reason: data.reason || undefined,
                legalTrack: data.legalTrack || undefined,
                targetKind: data.targetKind || undefined,
              },
            } as never)
          }}
        >
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            debounceTime={300}
            fieldProps={{ className: 'min-w-0 flex-1' }}
            inputProps={{ 'aria-label': t('Search reports') }}
            minChars={0}
            name="name"
            onSearchChange={handleNameSearchChange}
            placeholder={t('Search by case reference, subject, or reporter name…')}
            toggleFiltersLabel={t('Toggle filters')}
          >
            <Card className="gap-0 py-0">
              <CardContent className="px-2 py-3">
                <div className="flex w-full flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
                  <NativeSelectFormField
                    fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                    label={t('View')}
                    name="view"
                    onValueChange={() => filterResults()}
                  >
                    <option value="UNRESOLVED">{t('All unresolved')}</option>
                    <option value="ESCALATED">{t('Escalated')}</option>
                    <option value="AWAITING_THIRD_PARTY">{t('Awaiting third party')}</option>
                    <option value="SLA_OVERDUE">{t('SLA overdue')}</option>
                    <option value="RESOLVED">{t('Resolved')}</option>
                  </NativeSelectFormField>
                  <NativeSelectFormField
                    fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                    label={t('Track')}
                    name="legalTrack"
                    onValueChange={() => filterResults()}
                  >
                    <option value="">{t('All tracks')}</option>
                    <option value="CHILD_SAFETY">{t('Child safety')}</option>
                    <option value="NCII">{t('NCII')}</option>
                    <option value="TRAFFICKING">{t('Trafficking')}</option>
                    <option value="OTHER_ILLEGAL">{t('Other illegal')}</option>
                    <option value="COPYRIGHT">{t('Copyright')}</option>
                    <option value="COMMUNITY">{t('Community')}</option>
                  </NativeSelectFormField>
                  <NativeSelectFormField
                    fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                    label={t('Target')}
                    name="targetKind"
                    onValueChange={() => filterResults()}
                  >
                    <option value="">{t('All targets')}</option>
                    <option value="PROFILE">{t('Profile')}</option>
                    <option value="MEDIA">{t('Media')}</option>
                    <option value="POST">{t('Post')}</option>
                    <option value="COMMENT">{t('Comment')}</option>
                    <option value="MESSAGE">{t('Message')}</option>
                    <option value="EVENT">{t('Event')}</option>
                    <option value="GROUP">{t('Group')}</option>
                    <option value="RENDEZVOUS">{t('Rendezvous')}</option>
                  </NativeSelectFormField>
                  <NativeSelectFormField
                    fieldProps={{ className: 'min-w-0 flex-1 w-full sm:min-w-[10rem]' }}
                    label={t('Reason')}
                    name="reason"
                    onValueChange={() => filterResults()}
                  >
                    <option value="">{t('All reasons')}</option>
                    <option value="CHILD_SEXUAL_ABUSE_MATERIAL">{t('Child sexual abuse material')}</option>
                    <option value="APPARENT_MINOR">{t('Apparent minor')}</option>
                    <option value="NON_CONSENSUAL_INTIMATE_IMAGERY">{t('Non-consensual intimate imagery')}</option>
                    <option value="HARASSMENT_OR_BULLYING">{t('Harassment or bullying')}</option>
                    <option value="HATE_SPEECH">{t('Hate speech')}</option>
                    <option value="SPAM">{t('Spam')}</option>
                    <option value="FAKE_PROFILE">{t('Fake profile')}</option>
                    <option value="COPYRIGHT">{t('Copyright')}</option>
                    <option value="OTHER">{t('Other')}</option>
                  </NativeSelectFormField>
                </div>
              </CardContent>
            </Card>
          </SearchExpand>
        </Form>

        <AdminReports
          legalTrack={search.legalTrack}
          profileName={params.name}
          reason={search.reason}
          slaOverdue={params.slaOverdue}
          subjectProfileId={params.subjectProfileId}
          targetKind={search.targetKind}
          view={params.view}
        />
      </div>
    </div>
  )
}
